import { ReactNode } from 'react'
import { TFunction } from 'i18next'

import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import {
  getAllSpecializations,
  getClass,
} from 'app/frontend/base/services/characterValues'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'

const specializationById = getAllSpecializations()

const getClassColor = (className: string) =>
  `var(--color-class-${className.toLowerCase().replace(/\s+/g, '-')})`

type AssignmentRoleIcon = 'tank' | 'heal' | 'damage' | 'ranged' | 'melee'

const withRoleIcon = (label: ReactNode, role?: AssignmentRoleIcon) => {
  if (!role) return label
  return (
    <span className='inline-flex items-center gap-1.5 min-w-0'>
      <CharacterRoleIcon role={role} size='small' className='shrink-0' />
      <span className='truncate'>{label}</span>
    </span>
  )
}

const collectionRoleIcon = (
  collection: Extract<
    RaidPlanAssignmentEntity,
    { kind: 'collection' }
  >['collection'],
): AssignmentRoleIcon | undefined => {
  if (collection === 'tanks') return 'tank'
  if (collection === 'healers') return 'heal'
  if (collection === 'dps') return 'damage'
  if (collection === 'ranged') return 'ranged'
  if (collection === 'melee') return 'melee'
  return undefined
}

const byRoleIcon = (
  role: Extract<RaidPlanAssignmentEntity, { kind: 'by_role' }>['role'],
): AssignmentRoleIcon | undefined => {
  if (role === 'tank') return 'tank'
  if (role === 'healer') return 'heal'
  if (role === 'dps') return 'damage'
  if (role === 'ranged') return 'ranged'
  if (role === 'melee') return 'melee'
  return undefined
}

export const getAssignmentEntityLabel = (
  t: TFunction,
  entity: RaidPlanAssignmentEntity,
): ReactNode => {
  if (entity.kind === 'member') {
    if (!entity.spec_id) return entity.label
    const specialization = specializationById[entity.spec_id.toString()]
    if (!specialization) return entity.label
    const className = getClass(specialization.class_id?.toString() ?? '')
    if (!className) return entity.label
    return (
      <span className='inline-flex items-center gap-1.5 min-w-0'>
        <BlizzardIcon
          iconName={specialization.icon}
          size='small'
          className='shrink-0'
        />
        <span
          className='truncate'
          style={{ color: getClassColor(className) }}
        >{`${specialization.name} ${className}`}</span>
      </span>
    )
  }
  if (entity.kind === 'everyone')
    return t('dashboard.team.plans.assignments.entities.everyone')
  if (entity.kind === 'collection') {
    return withRoleIcon(
      t(
        `dashboard.team.plans.assignments.entities.collection.${entity.collection}`,
      ),
      collectionRoleIcon(entity.collection),
    )
  }
  if (entity.kind === 'by_role') {
    return withRoleIcon(
      t(`dashboard.team.plans.assignments.entities.by-role.${entity.role}`),
      byRoleIcon(entity.role),
    )
  }
  if (entity.kind === 'by_class') {
    const className = getClass(entity.class_id.toString())
    if (!className) return t('shared.unknown')
    return (
      <span className='truncate' style={{ color: getClassColor(className) }}>
        {className}
      </span>
    )
  }
  if (entity.kind === 'group')
    return t('dashboard.team.plans.assignments.entities.group', {
      count: entity.group,
    })
  if (entity.kind === 'role') {
    const role =
      entity.role === 'heal'
        ? 'healer'
        : entity.role === 'tank'
        ? 'tank'
        : entity.role === 'ranged'
        ? 'ranged'
        : 'melee'
    return withRoleIcon(
      t(`dashboard.team.plans.assignments.entities.by-role.${role}`),
      entity.role === 'heal' ? 'heal' : entity.role,
    )
  }
  return t('shared.unknown')
}
